/**
 * One scenario's run history: every persisted run, newest first, each row expanding to the
 * thread the bulk-run pane shows. The body remounts per scenario, so a reopened sheet starts fresh.
 */

import { useCallback, useEffect, useState } from 'react';
import { Loader2, Ticket } from 'lucide-react';
import { getApiErrorMessage } from '@/lib/utils';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { useRunAuthor } from '@/modules/simulator/runs/use-run-author';
import { aiFunctionService, type ScenarioRunHistoryItem } from '@/services/ai-function.service';
import type { ScenarioKind } from '@/types/websocket.types';
import { KindBadge } from '../kind-badge';
import { ModeBadge, OriginBadge, RunVerdictBadge } from './run-detail';
import { storedRunFacts } from './run-facts';
import { RunThread } from './run-thread';

export interface RunHistoryScenario {
	id: string;
	description: string;
	ticketId: string | null;
	kind: ScenarioKind | null;
}

const PAGE_SIZE = 20;

const runDate = (iso: string): string =>
	new Date(iso).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });

/** The query is the sheet's own header, so each run shows only what it did with it. */
const HistoryRun = ({ run }: { run: ScenarioRunHistoryItem }) => {
	const [verdictOpen, setVerdictOpen] = useState(true);
	const [reportOpen, setReportOpen] = useState(false);
	return (
		<RunThread
			run={storedRunFacts(run)}
			detail={run}
			queryTurns={null}
			active={false}
			trail={[]}
			investigationTrail={undefined}
			verdictOpen={verdictOpen}
			onVerdictOpenChange={setVerdictOpen}
			reportOpen={reportOpen}
			onReportOpenChange={setReportOpen}
			showTurns={false}
		/>
	);
};

const HistoryBody = ({ sopId, scenario }: { sopId: string; scenario: RunHistoryScenario }) => {
	const [runs, setRuns] = useState<ScenarioRunHistoryItem[]>([]);
	const [total, setTotal] = useState<number | null>(null);
	const [error, setError] = useState<string | null>(null);
	const [loading, setLoading] = useState(true);
	const [loadingMore, setLoadingMore] = useState(false);
	const authorOf = useRunAuthor();

	const load = useCallback(
		async (offset: number) => {
			const page = await aiFunctionService.fetchScenarioRuns(sopId, scenario.id, offset, PAGE_SIZE);
			// A run persisted while the sheet is open shifts the offset window; dropping
			// already-shown rows keeps the keys unique instead of showing a duplicate.
			setRuns((prev) =>
				offset === 0
					? page.runs
					: [...prev, ...page.runs.filter((run) => !prev.some((seen) => seen.requestId === run.requestId))]
			);
			setTotal(page.total);
		},
		[sopId, scenario.id]
	);

	useEffect(() => {
		setLoading(true);
		setError(null);
		load(0)
			.catch((err) => setError(getApiErrorMessage(err, 'The run history could not be loaded.')))
			.finally(() => setLoading(false));
	}, [load]);

	const loadMore = () => {
		setLoadingMore(true);
		setError(null);
		load(runs.length)
			.catch((err) => setError(getApiErrorMessage(err, 'More runs could not be loaded.')))
			.finally(() => setLoadingMore(false));
	};

	if (loading)
		return (
			<div className='flex h-32 items-center justify-center'>
				<Loader2 className='size-4 animate-spin text-muted-foreground' />
			</div>
		);
	// A failed page fetch must not throw away runs already on screen.
	if (error && runs.length === 0) return <p className='px-1 py-6 text-center text-xs text-red-700'>{error}</p>;
	if (runs.length === 0)
		return <p className='px-1 py-6 text-center text-xs text-muted-foreground'>No runs of this scenario yet.</p>;

	return (
		<div className='flex flex-col'>
			<Accordion type='multiple' className='divide-y'>
				{runs.map((run) => (
					<AccordionItem key={run.requestId} value={run.requestId} className='border-b-0'>
						<AccordionTrigger className='gap-2 py-2.5 hover:no-underline'>
							<div className='flex min-w-0 flex-1 items-center gap-2 text-left'>
								<span className='shrink-0 text-xs tabular-nums text-muted-foreground'>{runDate(run.createdAt)}</span>
								<ModeBadge mode={run.mode} />
								<OriginBadge origin={run.origin} />
								{run.origin.kind === 'simulator' && (
									<span className='truncate text-xs text-muted-foreground'>{authorOf(run.origin.userId)}</span>
								)}
								<span className='ml-auto' />
								<RunVerdictBadge detail={run} />
							</div>
						</AccordionTrigger>
						<AccordionContent className='pb-4'>
							<HistoryRun run={run} />
						</AccordionContent>
					</AccordionItem>
				))}
			</Accordion>
			{error && <p className='mt-2 text-center text-xs text-red-700'>{error}</p>}
			{total !== null && runs.length < total && (
				<Button variant='outline' size='sm' className='mt-2 self-center text-xs' disabled={loadingMore} onClick={loadMore}>
					{loadingMore && <Loader2 className='size-3 animate-spin' />}
					Load older runs ({total - runs.length} more)
				</Button>
			)}
		</div>
	);
};

interface RunHistorySheetProps {
	sopId: string;
	/** The scenario whose history is open; null keeps the sheet closed. */
	scenario: RunHistoryScenario | null;
	onOpenChange: (open: boolean) => void;
	/** Opens the real conversation behind the ticket chip. */
	onOpenTicket?: (ticketId: string) => void;
}

export const RunHistorySheet = ({ sopId, scenario, onOpenChange, onOpenTicket }: RunHistorySheetProps) => (
	<Sheet open={scenario !== null} onOpenChange={onOpenChange}>
		<SheetContent side='right' className='flex w-full flex-col gap-0 sm:max-w-2xl xl:max-w-4xl'>
			<SheetHeader className='pb-2'>
				<SheetTitle className='text-sm'>Run history</SheetTitle>
				<SheetDescription asChild>
					<span className='flex items-center gap-2 text-xs'>
						<span className='min-w-0 truncate'>{scenario?.description}</span>
						<KindBadge kind={scenario?.kind} />
						{scenario?.ticketId && (
							<button
								type='button'
								className='inline-flex shrink-0 cursor-pointer items-center gap-1 rounded-full bg-muted px-1.5 text-[10px] text-muted-foreground hover:text-foreground hover:underline'
								onClick={() => onOpenTicket?.(scenario.ticketId!)}
							>
								<Ticket className='size-2.5' />
								{scenario.ticketId}
							</button>
						)}
					</span>
				</SheetDescription>
			</SheetHeader>
			<div className='min-h-0 flex-1 overflow-y-auto px-4 pb-4'>
				{scenario && <HistoryBody key={scenario.id} sopId={sopId} scenario={scenario} />}
			</div>
		</SheetContent>
	</Sheet>
);
